"use client"
import { useDebounce } from "@/hook/useDebounce"
import { t } from "@/languages/i18n"
import { createCategory } from "@/services/category"
import { useDomainStore } from "@/stores/use-domain-store"
import { AttributesCategory, CategoriesProps, CategoryOption } from "@/types/types"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { PlusOutlined } from "@ant-design/icons"
import { App, Button, Input, Select, Space } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { fetchCategories } from "../services/post"

const Categories: React.FC<CategoriesProps> = ({
  selectedCategories,
  onChangeCategories,
  selectedCategoryOptions = [],
  locale,
}) => {
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [isAddingCategory, setIsAddingCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState("")
  const [permissions, setPermissions] = useState<Record<string, any>>({})
  const [searchValue, setSearchValue] = useState("")

  const debouncedSearch = useDebounce(searchValue, 300)
  const { message } = App.useApp();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedPermissions = JSON.parse(localStorage.getItem("permissions") || "{}")
      setPermissions(storedPermissions)
    }
  }, [])

  const handleFetchCategories = async () => {
    try {
      const response = await fetchCategories(locale, enforcedDomain)
      const categoriesData = response.data
      if (Array.isArray(categoriesData)) {
        const categoryOptions = categoriesData.map((category: any) => {
          const isPublished = !!category.attributes.publishedAt
          return {
            id: category.id,
            label: category.attributes.name,
            value: category.id,
            disabled: false,
            status: isPublished ? "published" : "draft",
          }
        })
        setCategories(categoryOptions)
      }
    } catch {
      message.error(t("Error fetching categories"))
    }
  }

  const handleAddCategory = async () => {
    if (newCategoryName) {
      if (newCategoryName.length < 10) {
        return message.error(t("Name phải có ít nhất 10 ký tự"))
      }
      try {
        const slug = newCategoryName
          ?.normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
        const data: AttributesCategory = {
          name: newCategoryName,
          slug: slug,
          description: "",
        }

        await createCategory(data)
        await handleFetchCategories()

        setNewCategoryName("")
        setIsAddingCategory(false)
        message.success(t(`Added new category: ${newCategoryName}`))
      } catch {
        message.error(t("Error adding category"))
      }
    } else {
      message.error(t("Please enter a category name"))
    }
  }

  useEffect(() => {
    handleFetchCategories()
  }, [locale, enforcedDomain])

  const mergedCategories = useMemo(() => {
    if (selectedCategoryOptions.length === 0) return categories
    const map = new Map<number, CategoryOption>()
    selectedCategoryOptions.forEach(item => map.set(item.id, item))
    categories.forEach(item => map.set(item.id, item))
    return Array.from(map.values())
  }, [categories, selectedCategoryOptions])

  // Hiển thị HẾT danh mục khớp tìm kiếm (số lượng ít) + sắp xếp tên A→Z để dễ
  // tra, thay vì cắt còn 20 mục đầu khiến user phải tự nhớ tên để gõ. localeCompare
  // với "vi" để sắp đúng dấu tiếng Việt.
  const filteredCategories = mergedCategories
    .filter(item => item?.label?.toLowerCase().includes(debouncedSearch.toLowerCase()))
    .sort((a, b) => (a?.label || "").localeCompare(b?.label || "", "vi", { sensitivity: "base" }))

  // Redesigned: no own card chrome / title. Parent FormSection provides group.
  return (
    <div>
      <Select
        mode="multiple"
        className="w-full"
        placeholder={t("Tìm danh mục")}
        value={selectedCategories}
        onChange={onChangeCategories}
        onSearch={setSearchValue}
        filterOption={false}
        showSearch
        allowClear
        options={filteredCategories.map(item => ({
          value: item.value,
          label: (
            <Space>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  display: "inline-block",
                  backgroundColor:
                    item.status === "published" ? "var(--admin-accent)" : "var(--admin-muted-fg)",
                }}
              />
              {t(`${item.label}`)}
            </Space>
          ),
        }))}
        notFoundContent={
          <div style={{ padding: "6px 10px", color: "var(--admin-muted-fg)", fontSize: 13 }}>
            {t("Không có danh mục phù hợp")}
          </div>
        }
      />

      {permissions.cateogry?.create.enabled && (
        <div className="mt-3">
          {!isAddingCategory ? (
            <Button
              type="link"
              onClick={() => setIsAddingCategory(true)}
              icon={<PlusOutlined />}
              style={{ padding: 0, height: "auto" }}
            >
              {t("Thêm danh mục mới")}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder={t("Tên danh mục")}
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
              />
              <Button type="primary" onClick={handleAddCategory}>
                {t("Thêm")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Categories
